import { useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";
import {
  Radar, Search, ArrowLeft, Loader2, AlertOctagon, AlertTriangle,
  Info, ShieldCheck, Circle, Bell, BellRing,
} from "lucide-react";
import { usePaywall } from "@/hooks/usePaywall";

const NAVY = "#0B1B2F";
const GOLD = "#C3A05A";

const SEV_STYLE = {
  CRITICAL: { color: "#DC2626", bg: "#FEE2E2", label: "CRÍTICO", icon: AlertOctagon },
  HIGH:     { color: "#D97706", bg: "#FEF3C7", label: "ALTO",    icon: AlertTriangle },
  MEDIUM:   { color: "#2563EB", bg: "#DBEAFE", label: "MÉDIO",   icon: Info },
  LOW:      { color: "#059669", bg: "#D1FAE5", label: "BAIXO",   icon: ShieldCheck },
  INFO:     { color: "#475569", bg: "#F1F5F9", label: "INFO",    icon: Circle },
};

const RISK_LEVEL_STYLE = {
  "CRÍTICO":  { bg: "linear-gradient(135deg,#7F1D1D,#DC2626)", ring: "#DC2626" },
  "ALTO":     { bg: "linear-gradient(135deg,#78350F,#D97706)", ring: "#D97706" },
  "MÉDIO":    { bg: "linear-gradient(135deg,#1E3A8A,#2563EB)", ring: "#2563EB" },
  "BAIXO":    { bg: "linear-gradient(135deg,#065F46,#059669)", ring: "#059669" },
  "MÍNIMO":   { bg: "linear-gradient(135deg,#334155,#64748B)", ring: "#64748B" },
};

export default function RadarRisco() {
  const [cnpj, setCnpj] = useState("");
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [monitoring, setMonitoring] = useState(false);
  const [watchId, setWatchId] = useState(null);
  const { authorize, PaywallElement } = usePaywall("CPF_SCAN");

  const doSearch = async () => {
    const digits = cnpj.replace(/\D/g, "");
    if (digits.length !== 14) {
      toast.error("Informe um CNPJ válido (14 dígitos)");
      return;
    }
    // Paywall: R$ 3,00 por varredura de identificador
    const authorization = await authorize({ quantity: 1 });
    if (!authorization) { toast.info("Varredura cancelada."); return; }
    setBusy(true); setResult(null); setWatchId(null);
    try {
      const { data } = await api.get(`/intel/radar/${digits}`);
      await authorization.commit();
      setResult(data);
    } catch (e) {
      await authorization.release();
      toast.error("Erro ao consultar Radar de Risco");
    } finally { setBusy(false); }
  };

  const createRadarWatch = async () => {
    if (!result?.identifier) return;
    setMonitoring(true);
    try {
      const { data } = await api.post("/intel/watches", {
        kind: "radar", cnpj: result.identifier,
      });
      setWatchId(data.watch_id);
      toast.success(
        `Radar monitorado! Você receberá e-mail sempre que houver mudança.`,
        { description: `Watch ID: ${data.watch_id}` },
      );
    } catch (e) {
      const msg = e.response?.data?.detail;
      if (typeof msg === "object" && msg?.error === "watch_limit_exceeded") {
        toast.error(msg.message || "Limite de watches atingido no plano atual.");
      } else {
        toast.error(typeof msg === "string" ? msg : "Erro ao criar watch");
      }
    } finally { setMonitoring(false); }
  };

  const riskCfg = result?.risk_level ? RISK_LEVEL_STYLE[result.risk_level]
                                          : RISK_LEVEL_STYLE["MÍNIMO"];

  return (
    <div className="flex-1 min-w-0 flex flex-col" data-testid="radar-risco-page">
      <header className="bg-[#0B1B2F] text-white">
        <div className="max-w-6xl mx-auto px-6 py-6 flex items-center gap-3">
          <Link to="/inteligencia" data-testid="back-intel"
                className="text-white/70 hover:text-white flex items-center gap-1 text-sm">
            <ArrowLeft className="w-4 h-4"/> Inteligência 360
          </Link>
          <span className="text-white/30">·</span>
          <div className="flex items-center gap-2">
            <div className="w-9 h-9 rounded-lg flex items-center justify-center"
                 style={{ background: `${GOLD}33`, border: `1px solid ${GOLD}` }}>
              <Radar className="w-4 h-4" style={{ color: GOLD }}/>
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-widest" style={{ color: GOLD }}>
                Radar de Risco · CNPJ
              </div>
              <h1 className="font-heading text-xl">RFB + CGU + PGFN + TCU + PNCP + SIGMINE + IBAMA</h1>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-6 py-8">
        <div className="bg-white rounded-2xl border border-slate-200 p-5 shadow-sm">
          <label className="text-xs uppercase tracking-widest text-slate-500">CNPJ para radar de risco</label>
          <div className="mt-2 flex gap-2">
            <input value={cnpj} onChange={(e) => setCnpj(e.target.value)}
              placeholder="00.000.000/0000-00" data-testid="input-cnpj-radar"
              className="flex-1 border border-slate-300 rounded-lg px-3 py-2.5 text-sm font-mono"/>
            <button onClick={doSearch} disabled={busy || !cnpj}
              data-testid="btn-radar-scan"
              className="px-5 py-2.5 rounded-lg text-white text-sm font-semibold
                             disabled:opacity-60 inline-flex items-center gap-2"
              style={{ background: NAVY }}>
              {busy ? <Loader2 className="w-4 h-4 animate-spin"/> : <Radar className="w-4 h-4"/>}
              Escanear risco
            </button>
          </div>
          <p className="text-xs text-slate-500 mt-2">
            Cruzamento paralelo de <b>10 fontes públicas</b>. Score de risco 0-100 baseado em severidade
            das ocorrências. Nada inventado.
          </p>
        </div>

        {result && (
          <div className="mt-6 grid lg:grid-cols-3 gap-4" data-testid="radar-results">
            {/* Score gauge */}
            <div className="lg:col-span-1 bg-white rounded-2xl border border-slate-200 p-5">
              <div className="flex items-start justify-between gap-2">
                <div className="text-[10px] uppercase tracking-widest text-slate-500">
                  Score de risco
                </div>
                {watchId ? (
                  <span className="inline-flex items-center gap-1 text-[10px] px-2 py-1 rounded-full bg-emerald-50 text-emerald-700 border border-emerald-200"
                        data-testid="radar-monitoring-badge">
                    <BellRing className="w-3 h-3"/> Monitorado
                  </span>
                ) : (
                  <button onClick={createRadarWatch} disabled={monitoring}
                    data-testid="btn-monitorar-radar"
                    className="inline-flex items-center gap-1 text-[10px] px-2.5 py-1 rounded-full transition disabled:opacity-60"
                    style={{ background: `${GOLD}22`, border: `1px solid ${GOLD}66`, color: GOLD }}>
                    {monitoring ? <Loader2 className="w-3 h-3 animate-spin"/> : <Bell className="w-3 h-3"/>}
                    {monitoring ? "Criando..." : "Monitorar radar"}
                  </button>
                )}
              </div>
              <div className="mt-3 flex items-center gap-4">
                <div className="w-28 h-28 rounded-full flex items-center justify-center text-white shrink-0 shadow-lg"
                     style={{ background: riskCfg.bg, boxShadow: `0 0 40px -10px ${riskCfg.ring}` }}
                     data-testid="risk-gauge">
                  <div className="text-center">
                    <div className="font-heading text-4xl leading-none">{result.risk_score}</div>
                    <div className="text-[9px] tracking-widest mt-1 opacity-80">/ 100</div>
                  </div>
                </div>
                <div>
                  <div className="text-[10px] uppercase tracking-widest text-slate-400">Classificação</div>
                  <div className="font-heading text-2xl" style={{ color: riskCfg.ring }}
                       data-testid="risk-level">{result.risk_level}</div>
                  <div className="text-xs text-slate-600 mt-1">
                    {result.razao_social || result.identifier}
                  </div>
                  <div className="text-[11px] text-slate-500 mt-0.5">
                    {result.situacao_cadastral}
                  </div>
                </div>
              </div>

              {/* Severity breakdown */}
              <div className="mt-5 space-y-1.5">
                {Object.entries(SEV_STYLE).map(([sev, cfg]) => {
                  const n = result.occurrences_by_severity?.[sev] || 0;
                  return (
                    <div key={sev} className="flex items-center gap-2">
                      <div className="w-2 h-2 rounded-full" style={{ background: cfg.color }}/>
                      <span className="text-xs flex-1" style={{ color: cfg.color }}>{cfg.label}</span>
                      <span className="text-xs font-semibold tabular-nums">{n}</span>
                    </div>
                  );
                })}
              </div>

              {/* Sources status */}
              <div className="mt-5 pt-4 border-t border-slate-100">
                <div className="text-[10px] uppercase tracking-widest text-slate-500 mb-2">
                  Status das fontes
                </div>
                <div className="grid grid-cols-2 gap-1 text-[11px]">
                  {Object.entries(result.sources_status || {}).map(([src, st]) => (
                    <div key={src} className="flex items-center gap-1.5">
                      <span className={`w-1.5 h-1.5 rounded-full ${
                        st === "FOUND" ? "bg-emerald-500" :
                        st === "NOT_FOUND" ? "bg-slate-300" :
                        "bg-amber-400"}`}/>
                      <span className="text-slate-600 truncate">{src}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Timeline */}
            <div className="lg:col-span-2 bg-white rounded-2xl border border-slate-200 p-5">
              <div className="flex items-baseline justify-between mb-3">
                <div>
                  <div className="text-[10px] uppercase tracking-widest text-slate-500">
                    Timeline de ocorrências
                  </div>
                  <h3 className="font-heading text-xl" style={{ color: NAVY }}>
                    {result.timeline?.length || 0} evento(s) rastreados
                  </h3>
                </div>
              </div>

              {(result.timeline || []).length === 0 ? (
                <div className="py-8 text-center text-sm text-slate-500">
                  Nenhuma ocorrência encontrada nas fontes disponíveis.
                </div>
              ) : (
                <div className="space-y-2 max-h-[600px] overflow-y-auto pr-1">
                  {result.timeline.map((ev, i) => {
                    const cfg = SEV_STYLE[ev.severity] || SEV_STYLE.INFO;
                    const Icon = cfg.icon;
                    return (
                      <div key={i} data-testid={`timeline-item-${i}`}
                           className="flex gap-3 p-3 rounded-xl border border-slate-100 hover:border-slate-200 transition">
                        <div className="w-9 h-9 rounded-lg flex items-center justify-center shrink-0"
                             style={{ background: cfg.bg }}>
                          <Icon className="w-4 h-4" style={{ color: cfg.color }}/>
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 flex-wrap">
                            <span className="text-[9px] uppercase tracking-widest px-1.5 py-0.5 rounded"
                                  style={{ background: cfg.bg, color: cfg.color }}>
                              {cfg.label}
                            </span>
                            <span className="text-[10px] text-slate-400">{ev.source}</span>
                            <span className="text-[10px] text-slate-500 ml-auto">
                              {(ev.date || "").slice(0, 10)}
                            </span>
                          </div>
                          <div className="text-sm font-medium mt-1" style={{ color: NAVY }}>
                            {ev.title}
                          </div>
                          {ev.description && (
                            <div className="text-xs text-slate-600 mt-0.5">
                              {ev.description}
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        )}
      </main>
      {PaywallElement}
    </div>
  );
}
